import { useApolloClient } from "@apollo/client"
import { useScopedTranslation } from "@pathwright/ux/src/components/lng/useScopedTranslation"
import getVideoId from "get-video-id"
import { useCallback, useEffect, useRef, useState } from "react"
import { matchPath } from "react-router-dom"
import {
  CoverPageOfferFragment as CoverPageOffer,
  CoverPageOffersDocument,
  OfferAccessState
} from "@pathwright/graphql-client/src/generated"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import { getPathUrl } from "../utils/urls"
import { useCoverPageContext } from "./CoverPageProvider"

// We reserve "price description" space only when the offer can actually
// render a description line under the button (single-option offers).
export const offerHasPriceDescription = (offer: CoverPageOffer) => {
  if ((offer.price_options?.length || 0) > 1) return false

  return offer.price_options.some(
    (priceOption: CoverPageOffer["price_options"][0]) =>
      !!priceOption.price_description
  )
}

export const offerHasMeta = (offer: CoverPageOffer) => {
  return offer.access.meta.length > 0
}

/** Whether the offer card renders a row below pricing (access meta or conversion meta). */
export const offerShowsBottomMetaRow = (offer: CoverPageOffer) => {
  if (offer.access.conversion_date) return true

  return offerHasMeta(offer)
}

export interface VideoInfo {
  id: string
  service: string
}

const isIframelyHost = (hostname: string) =>
  hostname === "iframely.net" ||
  hostname.endsWith(".iframely.net") ||
  hostname === "iframe.ly" ||
  hostname.endsWith(".iframe.ly")

const unwrapIframelyUrl = (url: string): string => {
  try {
    const parsed = new URL(url)
    if (!isIframelyHost(parsed.hostname)) return url

    const nestedUrl = parsed.searchParams.get("url")
    if (!nestedUrl) return url

    return decodeURIComponent(nestedUrl)
  } catch {
    return url
  }
}

export const parseVideoEmbedCode = (embedCode: string): VideoInfo | null => {
  if (!embedCode) return null

  try {
    // First, try to extract iframe src URL from HTML embed code
    // This handles cases where the embed code is wrapped in divs or other elements
    let urlToParse = embedCode.trim()

    // Check if the embed code contains HTML (likely an iframe)
    if (embedCode.includes("<iframe") || embedCode.includes("<IFRAME")) {
      try {
        const parser = new DOMParser()
        const doc = parser.parseFromString(embedCode, "text/html")
        const iframe = doc.querySelector("iframe")

        if (iframe && iframe.src) {
          urlToParse = iframe.src
        }
      } catch (parseError) {
        // If DOM parsing fails, try regex fallback
        const iframeMatch = embedCode.match(/<iframe[^>]+src=["']([^"']+)["']/i)
        if (iframeMatch && iframeMatch[1]) {
          urlToParse = iframeMatch[1]
        }
      }
    }

    const result = getVideoId(unwrapIframelyUrl(urlToParse))

    if (result.id && result.service) {
      return {
        id: result.id,
        service: result.service
      }
    }

    return null
  } catch (error) {
    console.warn("Failed to parse video embed code:", error)
    return null
  }
}

// Custom hook to detect if text will wrap to multiple lines
export const useTextWrapDetection = (
  text: string,
  dependencies: any[] = []
) => {
  const [willWrap, setWillWrap] = useState(false)
  const [lastContainerWidth, setLastContainerWidth] = useState(0)
  const headingRef = useRef<HTMLHeadingElement>(null)
  const imageRef = useRef<HTMLDivElement | HTMLImageElement>(null)
  const titleRowRef = useRef<HTMLDivElement>(null)

  // Adjust to title width:
  // If the title (heading) + the image (card) >= their parent container's width (titleRowRef)
  // then wrap the title to multiple lines

  // Hysteresis buffers to avoid flicker near the wrap threshold.
  // We wrap sooner (larger buffer) and only unwrap when there is
  // significantly more space available. If in doubt, we remain wrapped.
  const WRAP_BUFFER_PX = 60
  const UNWRAP_BUFFER_PX = 120

  const calculateWrap = useCallback(() => {
    if (!headingRef.current || !imageRef.current || !titleRowRef.current) {
      return
    }

    // Get the container width - this is the full available width
    const containerWidth = titleRowRef.current.clientWidth

    // Account for container padding
    const containerStyle = window.getComputedStyle(titleRowRef.current)
    const containerPaddingLeft = parseInt(containerStyle.paddingLeft) || 0
    const containerPaddingRight = parseInt(containerStyle.paddingRight) || 0
    const availableWidth =
      containerWidth - containerPaddingLeft - containerPaddingRight

    // Get the image width (including margin)
    const imageElement = imageRef.current
    const imageWidth = imageElement.clientWidth
    const imageMargin =
      parseInt(window.getComputedStyle(imageElement).marginRight) || 0
    const totalImageWidth = imageWidth + imageMargin

    // Measure the text, not the heading box. Writing width onto the live
    // heading fights layout animations and stretches the glyphs.
    const range = document.createRange()
    range.selectNodeContents(headingRef.current)
    const naturalHeadingWidth = range.getBoundingClientRect().width

    // Calculate total content width including gap
    const gap = 10 // Stack gap from the JSX
    const totalContentWidth = totalImageWidth + gap + naturalHeadingWidth

    // Apply hysteresis: bias toward wrapped state to prevent flicker
    const wrapThreshold = availableWidth - WRAP_BUFFER_PX
    const unwrapThreshold = availableWidth - UNWRAP_BUFFER_PX

    const nextWillWrap = willWrap
      ? totalContentWidth > unwrapThreshold
      : totalContentWidth > wrapThreshold

    // Only update if the wrap state is actually changing
    if (willWrap !== nextWillWrap) {
      // Determine resize direction based on container width change
      const isExpanding = containerWidth > lastContainerWidth
      const isShrinking = containerWidth < lastContainerWidth

      // Only allow layout changes that make sense for the resize direction:
      // - When expanding: only allow unwrapping (column -> row)
      // - When shrinking: only allow wrapping (row -> column)
      // - On first load: allow any change
      const shouldAllowChange =
        lastContainerWidth === 0 || // First load
        (isExpanding && !nextWillWrap) || // Expanding: allow unwrapping
        (isShrinking && nextWillWrap) || // Shrinking: allow wrapping
        containerWidth === lastContainerWidth // No size change (text change)

      if (shouldAllowChange) {
        setWillWrap(nextWillWrap)
      }
    }

    // Update the last container width for next comparison
    setLastContainerWidth(containerWidth)
  }, [text, willWrap, lastContainerWidth, ...dependencies])

  useEffect(() => {
    if (!text) return

    // Calculate after a brief delay to ensure DOM is ready
    const timer = setTimeout(calculateWrap, 100)

    // Also calculate on window resize - use a debounced approach
    let resizeTimer: NodeJS.Timeout
    const handleResize = () => {
      clearTimeout(resizeTimer)
      resizeTimer = setTimeout(() => {
        calculateWrap()
      }, 50) // Shorter delay for resize events
    }

    window.addEventListener("resize", handleResize)

    return () => {
      clearTimeout(timer)
      clearTimeout(resizeTimer)
      window.removeEventListener("resize", handleResize)
    }
  }, [calculateWrap, text])

  // Also watch for container size changes
  useEffect(() => {
    if (!headingRef.current) return

    const updateWrap = () => {
      calculateWrap()
    }

    // Set up ResizeObserver to watch the parent Stack for size changes
    const resizeObserver = new ResizeObserver(updateWrap)
    const parentStack = headingRef.current.parentElement
    if (parentStack) {
      resizeObserver.observe(parentStack)
    }

    return () => {
      resizeObserver.disconnect()
    }
  }, [calculateWrap])

  return { willWrap, headingRef, imageRef, titleRowRef }
}

// Extract parameters from the current pathname using a route pattern.
// The provided patterns (e.g., "offer/:id(\\d+)/") are partial paths,
// so we allow any leading prefix and match non-exactly.
export function extractParamsFromPath(
  pathRe: string | string[],
  pathname: string
): Record<string, string> {
  const matchPatternAndExtractParams = (pattern: string) => {
    const normalized = pattern.startsWith("/") ? pattern.slice(1) : pattern
    const composedPattern = `/:__prefix(.*)/${normalized}`
    const m = matchPath(pathname, {
      path: composedPattern,
      exact: false,
      strict: false
    })
    const { __prefix: _drop, ...params } =
      (m?.params as Record<string, string>) || {}
    return params
  }

  if (Array.isArray(pathRe)) {
    for (const p of pathRe) {
      const params = matchPatternAndExtractParams(p)
      if (Object.keys(params).length > 0) return params
    }
    return {}
  }

  return matchPatternAndExtractParams(pathRe)
}

export type ItemWithId = { id?: string | number }

type Key = string | number

/**
 * Gets the next and previous items in a list relative to the current item.
 * @param items - Array of items to navigate through
 * @param currentItem - The current item to find neighbors for
 * @param options - Configuration options
 * @param options.circular - Whether to wrap around to beginning/end (default: false)
 * @param options.getKey - Function to extract key for item comparison (default: item.id)
 * @returns Object with nextItem and prevItem, or null if invalid input
 */
export const getNextAndPrevItems = <T>(
  items: T[],
  currentItem: T | null | undefined,
  {
    circular = false,
    getKey
  }: { circular?: boolean; getKey?: (item: T) => Key } = {}
): { nextItem: T | null; prevItem: T | null } | null => {
  if (!Array.isArray(items) || items.length === 0 || !currentItem) {
    return null
  }

  if (items.length === 1) {
    return { nextItem: null, prevItem: null }
  }

  const keyOf = (item: T) =>
    (getKey ? getKey(item) : (item as any)?.id) as Key | undefined

  const currentId = keyOf(currentItem)
  if (currentId === undefined || currentId === null) {
    return null
  }

  const currentIndex = items.findIndex(
    // compare stringified keys to handle mixed types
    (item) => `${keyOf(item)}` === `${currentId}`
  )
  if (currentIndex === -1) {
    return null
  }

  const lastIndex = items.length - 1

  let nextIndex = currentIndex + 1
  let prevIndex = currentIndex - 1

  if (circular) {
    if (nextIndex > lastIndex) nextIndex = 0
    if (prevIndex < 0) prevIndex = lastIndex
  }

  const nextItem =
    nextIndex < 0 || nextIndex > lastIndex ? null : items[nextIndex]
  const prevItem =
    prevIndex < 0 || prevIndex > lastIndex ? null : items[prevIndex]

  return { nextItem, prevItem }
}

export const useGetPathUrl = (): string | null => {
  const paintResource = useCoverPageContext((v) => v.paintResource)
  const cohortId = useGetPathCohortId()
  const slug = paintResource?.slug

  // We'll show the Path option if:
  // a) the user is enrolled in at least one Cohort,
  // b) the path is previewable,
  // c) the user can edit the library
  return cohortId && slug ? getPathUrl(slug, Number(cohortId)) : null
}

export const useGetPathCohortId = (): number | null => {
  const resource = useCoverPageContext((v) => v.resource)

  // 1. Check if the user is enrolled in a Cohort (if so, we'll use its Path)
  // Arbitrarily use the first enrolled offer's group id to get the path url
  const offers = useCoverPageContext((v) => v.offers)
  const enrolledOffers = offers?.filter(
    (offer) => offer.access.state === OfferAccessState.Active
  )
  const enrolledCohortId = enrolledOffers?.[0]?.cohort_id
  // 2. Check if there's a previewable Path
  const canPreview = useCoverPageContext((v) => v.canPreview)
  const previewableCohortId = resource?.preview_group?.id

  // 3. Check if the user is a superuser or can edit the library
  // (if so, we'll use the Source Path)
  const { me } = usePathwrightContext()
  const canEditLibrary =
    me?.permissions?.can_edit_library || me?.permissions?.can_administrate
  const sourceCohortId = resource?.curriculum_group?.id

  const cohortId =
    enrolledCohortId ||
    (canPreview ? previewableCohortId : null) ||
    (canEditLibrary ? sourceCohortId : null)

  return cohortId ? Number(cohortId) : null
}

export const useCoverPageTranslate = () => useScopedTranslation("cover_page")

// For refetching the cover page offers query, mainly for integration with legacy client.
export function useRefetchCoverPageOffers() {
  const client = useApolloClient()
  return useCallback(
    () => client.refetchQueries({ include: [CoverPageOffersDocument] }),
    [client]
  )
}
